import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cartApi } from '@/api/cart.api';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { toast } from '@/store/uiStore';
import { queryKeys } from './queryKeys';
import { getApiErrorMessage } from '@/api/client';
import type { CartSummary, AddToCartInput } from '@/types/cart';

const THIRTY_SEC = 30_000;

export function useCart() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrating = useAuthStore((s) => s.isHydrating);
  const syncCount = useCartStore((s) => s.syncCount);

  const query = useQuery({
    queryKey: queryKeys.cart,
    queryFn: () => cartApi.getCart(),
    enabled: isAuthenticated && !isHydrating,
    staleTime: THIRTY_SEC,
  });

  // Mirror server count into the optimistic store so the navbar badge stays
  // accurate without each component subscribing to the full cart query.
  useEffect(() => {
    if (query.data) syncCount(query.data.totalItems);
    if (!isAuthenticated) syncCount(0);
  }, [query.data, isAuthenticated, syncCount]);

  return query;
}

export function useAddToCart() {
  const queryClient = useQueryClient();
  const startPending = useCartStore((s) => s.startPending);
  const endPending = useCartStore((s) => s.endPending);
  const bumpCount = useCartStore((s) => s.bumpCount);
  const openDrawer = useCartStore((s) => s.openDrawer);

  return useMutation({
    mutationFn: (input: AddToCartInput) => cartApi.addItem(input),

    onMutate: async (input) => {
      startPending(input.variantId);
      bumpCount(input.quantity);
      // Snapshot the current cart so we can roll back on error
      await queryClient.cancelQueries({ queryKey: queryKeys.cart });
      const previous = queryClient.getQueryData<CartSummary>(queryKeys.cart);
      return { previous, input };
    },

    onSuccess: (data, _input) => {
      queryClient.setQueryData<CartSummary>(queryKeys.cart, data);
      toast.success('Added to cart');
      openDrawer();
    },

    onError: (err, _input, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKeys.cart, ctx.previous);
      bumpCount(-(_input?.quantity ?? 0));
      toast.error('Could not add to cart', getApiErrorMessage(err));
    },

    onSettled: (_data, _err, input) => {
      endPending(input.variantId);
      queryClient.invalidateQueries({ queryKey: queryKeys.cart });
    },
  });
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quantity }: { id: number; quantity: number }) =>
      cartApi.updateItem(id, { quantity }),
    onSuccess: (data) => queryClient.setQueryData<CartSummary>(queryKeys.cart, data),
    onError: (err) => toast.error('Update failed', getApiErrorMessage(err)),
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cartApi.removeItem(id),
    onSuccess: (data) => {
      queryClient.setQueryData<CartSummary>(queryKeys.cart, data);
      toast.info('Item removed');
    },
    onError: (err) => toast.error('Remove failed', getApiErrorMessage(err)),
  });
}

export function useClearCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cartApi.clear(),
    onSuccess: (data) => queryClient.setQueryData<CartSummary>(queryKeys.cart, data),
  });
}

export function useApplyCoupon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => cartApi.applyCoupon(code),
    onSuccess: (data) => {
      queryClient.setQueryData<CartSummary>(queryKeys.cart, data);
      toast.success('Coupon applied', data.appliedCouponCode ?? '');
    },
    onError: (err) => toast.error('Coupon failed', getApiErrorMessage(err)),
  });
}
